import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Bars3Icon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import { Slider } from '../../services/api';

interface SortableSlideRowProps {
  slide: Slider;
  onEdit: (slide: Slider, e?: React.MouseEvent) => void;
  onDelete: (slide: Slider, e?: React.MouseEvent) => void;
  onToggle: (id: string, e?: React.MouseEvent) => void;
  onRowClick: (slide: Slider) => void;
  getImageUrl: (url: string) => string;
  getCountryFlags: (countries: string[]) => string;
}

const SortableSlideRow: React.FC<SortableSlideRowProps> = ({
  slide,
  onEdit,
  onDelete,
  onToggle,
  onRowClick,
  getImageUrl,
  getCountryFlags,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: slide._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
      onClick={() => onRowClick(slide)}
    >
      {/* Drag Handle */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          onClick={(e) => e.stopPropagation()}
        >
          <Bars3Icon className="h-5 w-5" />
        </div>
      </td>

      {/* Order */}
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="text-sm font-medium text-gray-900 dark:text-white">
          {slide.displayOrder}
        </span>
      </td>

      {/* Preview */}
      <td className="px-6 py-4 whitespace-nowrap">
        <img
          src={getImageUrl(slide.imageUrlCropped || slide.imageUrl)}
          alt="Slide preview"
          className="h-12 w-20 object-cover rounded border border-gray-200 dark:border-gray-700"
        />
      </td>

      {/* Details */}
      <td className="px-6 py-4">
        <div>
          <div className="text-sm font-medium text-gray-900 dark:text-white">
            {slide.buttonText || 'Learn More'}
          </div>
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {slide.title || slide.description || 'No description'}
          </div>
        </div>
      </td>

      {/* Language */}
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
          {slide.language?.toUpperCase() || 'N/A'}
        </span>
      </td>

      {/* Countries */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-900 dark:text-white">
          {getCountryFlags(slide.countries)}
        </div>
      </td>

      {/* Link */}
      <td className="px-6 py-4">
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
              slide.linkType === 'internal'
                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
            }`}
          >
            {slide.linkType}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[150px]">
            {slide.linkValue}
          </span>
        </div>
      </td>

      {/* Status */}
      <td className="px-6 py-4 whitespace-nowrap">
        <span
          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
            slide.isActive
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
          }`}
        >
          {slide.isActive ? '● Active' : '○ Inactive'}
        </span>
      </td>

      {/* Actions */}
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={(e) => onToggle(slide._id, e)}
            className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
            title={slide.isActive ? 'Deactivate' : 'Activate'}
          >
            {slide.isActive ? (
              <EyeSlashIcon className="h-5 w-5" />
            ) : (
              <EyeIcon className="h-5 w-5" />
            )}
          </button>
          <button
            onClick={(e) => onEdit(slide, e)}
            className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
            title="Edit"
          >
            <PencilIcon className="h-5 w-5" />
          </button>
          <button
            onClick={(e) => onDelete(slide, e)}
            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
            title="Delete"
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </div>
      </td>
    </tr>
  );
};

export default SortableSlideRow;
